import Cookies from 'js-cookie';
import i18n from 'i18next';
import { changePreferredCultureCode } from './changePreferredCultureCode';
import { defaultLocale, languagesFallback, localeCookie, mvcAppLocaleCookie } from '../constants';

const legacyAppPath = 'SSQV4';

/**
 * Checks if the locale should be automatically applied based on country selection.
 * Returns true ONLY when:
 * - User has NOT manually selected via locale picker (no 'userSelectedLng')
 * - Browser language was NOT detected (no 'browserLanguageDetected')
 *
 * This means locale auto-applies only for truly fresh users with no preferences.
 * Once browser language is detected OR user manually selects, country changes won't affect locale.
 *
 * @returns true if locale should auto-apply on country change, false otherwise
 */
export const shouldAutoApplyLocaleOnCountryChange = (): boolean => {
  const hasManualSelection = typeof localStorage !== 'undefined' && localStorage.getItem('userSelectedLng');
  const hasBrowserLanguage = typeof localStorage !== 'undefined' && localStorage.getItem('browserLanguageDetected');

  // Manual selection always wins.
  if (hasManualSelection) {
    return false;
  }

  // If the browser language is something other than the default locale,
  // do not let country selection override it.
  if (hasBrowserLanguage && hasBrowserLanguage !== defaultLocale) {
    return false;
  }

  // Allow country-driven locale updates when:
  // - user has not manually selected a locale
  // - browser language is not explicitly set to a non-default locale
  return true;
};

/**
 * Checks if a locale is supported and returns it, or returns the fallback locale for the language code.
 * This function matches the original behavior from languageDetector.ts for browser language detection.
 * @param locale - The locale string to check (e.g., 'en-US', 'fr', 'fr-FR')
 * @returns The supported locale, the fallback locale, or undefined
 */
export const checkLocale = (locale: string): string | undefined => {
  const supportedLngs = i18n.options?.supportedLngs as string[] | false | undefined;

  if (!Array.isArray(supportedLngs) || supportedLngs.length === 0) {
    return undefined;
  }

  if (locale.includes('-') && supportedLngs.includes(locale)) {
    return locale;
  }

  // Try to find a supported locale by language code (e.g., 'en' -> 'en-US')
  const [language] = locale.split('-');

  return languagesFallback[language];
};

/**
 * Gets a supported locale from a given locale string with validation.
 * This function validates that the fallback locale is actually supported before returning it.
 * @param locale - The locale string to check (e.g., 'en-US', 'fr', 'fr-FR')
 * @returns A validated supported locale or undefined
 */
export const getSupportedLocale = (locale?: string | null): string | undefined => {
  if (!locale) {
    return undefined;
  }

  // Normalize 'en' to 'en-US' to ensure consistent English locale
  const normalizedLocale = locale === 'en' ? 'en-US' : locale;

  const supportedLngs = i18n.options?.supportedLngs as string[] | false | undefined;

  if (!Array.isArray(supportedLngs) || supportedLngs.length === 0) {
    return undefined;
  }

  if (supportedLngs.includes(normalizedLocale)) {
    return normalizedLocale;
  }

  const [language] = normalizedLocale.split('-');
  const fallbackLocale = languagesFallback[language];

  if (fallbackLocale && supportedLngs.includes(fallbackLocale)) {
    return fallbackLocale;
  }

  return supportedLngs.includes(defaultLocale) ? defaultLocale : undefined;
};

const getLocaleCookieOptions = (): Cookies.CookieAttributes => {
  if (typeof window === 'undefined') {
    return {};
  }

  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  if (isLocalhost) {
    return {};
  }

  return {
    domain:
      '.' +
      hostname
        .split('.')
        .slice(-2)
        .join('.')
  };
};

export const applyLocalePreference = async (
  locale: string,
  userId?: string,
  isManualSelection: boolean = true
): Promise<string | undefined> => {
  const supportedLocale = getSupportedLocale(locale);

  if (!supportedLocale) {
    return undefined;
  }

  const cookieOptions = getLocaleCookieOptions();

  Cookies.set(localeCookie, supportedLocale, cookieOptions);
  Cookies.set(mvcAppLocaleCookie, `c=${supportedLocale}|uic=${supportedLocale}`, cookieOptions);

  if (userId) {
    await changePreferredCultureCode(userId, supportedLocale);
  }

  // Only set localStorage when user manually selects via locale picker
  // Do not set for automatic country-based locale changes
  if (isManualSelection && typeof localStorage !== 'undefined') {
    localStorage.setItem('userSelectedLng', supportedLocale);
  }

  if (typeof window !== 'undefined' && window.location.pathname.toUpperCase().includes(legacyAppPath)) {
    window.location.reload();
  } else {
    await i18n.changeLanguage(supportedLocale);
  }

  return supportedLocale;
};
